import { DEV } from "@/data/Environment";
import type {
    DailyHeadline,
    DailyHeadlineHasSubmitted,
    DailyHeadlineSubmission,
    NotificationObject,
    PreviousHeadlineSubmission,
    SearchProfile,
    SettingsStructure
} from "@/local-shared/model";
import type {
    DailyHeadlineCommentLikeResponse,
    DailyHeadlineSubmissionPost,
    LikeSubmissionResponse,
    ProfileDataResponse,
    ProfileHeadlineResponse,
    SubmissionCommentResponse
} from "@/local-shared/returnTypes";
import type { ReportedSubmissionDataType } from "@/realize-shared/model/RequestTypes";
import SearchAPI from "./search";
import AssetsAPI from "./assets";
import TwitchAPI from "./twitch";
import RoomAPI from "./room";

import HTTPStatus from "@/realize-shared/HTTPStatus";
import ProfileAPI from "./profile";

export const SUCCESS = HTTPStatus.OK;

export const NOT_FOUND_CODE = HTTPStatus.NOT_FOUND;


const API_ENDPOINT = DEV ? "http://localhost:3005" : import.meta.env.VITE_SOCIAL_SERVER as string;

export class SocialAPI
{
    private static instance: SocialAPI|null = null;

    public search: SearchAPI = new SearchAPI(this);

    public assets: AssetsAPI = new AssetsAPI(this);

    public twitch: TwitchAPI = new TwitchAPI(this);

    public rooms: RoomAPI = new RoomAPI(this);

    public profile: ProfileAPI = new ProfileAPI(this);

    protected API_ENDPOINT = API_ENDPOINT;

    private constructor()
    {
        // Private constructor to prevent instantiation
    }

    public static getInstance(): SocialAPI
    {
        if (!SocialAPI.instance)
        {
            SocialAPI.instance = new SocialAPI();
        }

        return SocialAPI.instance;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    public async fetch<T>(endpoint: string, options?: {
        method?: "GET" | "POST" | "PUT" | "DELETE",
        body?: string
    }): Promise<{
            status: HTTPStatus;
            message: string;
            data?: T;
        }>
    {
        const token = localStorage.getItem("access_token");

        return fetch(`${API_ENDPOINT}${endpoint}`, {
            method: options?.method ?? "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
            body: options?.body ?? undefined,
        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as { status: number; message: string; data?: T };
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return { status: 500, message: "Internal Server Error" };
            });
    }

    public async getDailyHeadline(): Promise<{
        status: HTTPStatus;
        message: string,
        data?: {
            id: number;
            headline: string;
            hasSubmitted: DailyHeadlineHasSubmitted;
            created_at: Date;
        }
    } | null>
    {
        interface ErrorResponse
        {
            status: HTTPStatus;
            message: string;
        }

        interface DailyHeadlineResponse
        {
            status: HTTPStatus;
            message: string;
            data: {
                id: number;
                headline: string;
                hasSubmitted: DailyHeadlineHasSubmitted;
                created_at: Date;
            }
        }

        const token = localStorage.getItem("access_token");


        const data: DailyHeadlineResponse | ErrorResponse | null = await fetch(`${API_ENDPOINT}/getDailyHeadline`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
        })
            .then((response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return response.json() as Promise<DailyHeadlineResponse|ErrorResponse>;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return {
                status: 500,
                message: "Failed to fetch daily headline",
            };
        }

        return data;
    }

    public async getDailySubmissions(headlineID: number, offset = 0, isToday = false)
    {
        interface DailyHeadlineSubmissionsResponse
        {
            status: number;
            message: string;
            hasMore: boolean;
            lastID: number | null;
            data: DailyHeadlineSubmissionPost[];
            winner: DailyHeadlineSubmissionPost | null;
            yesterdayHeadlineResult: string;
        }


        const token = localStorage.getItem("access_token");

        const data
        // eslint-disable-next-line @stylistic/max-len
        = await fetch(`${API_ENDPOINT}/getDailySubmissions?headline_id=${headlineID}&lastID=${offset}${isToday ? "&noHistory=1" : "&noHistory=0"}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as DailyHeadlineSubmissionsResponse;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async likeSubmission(submissionID: number): Promise<LikeSubmissionResponse | null>
    {
        const postBody = JSON.stringify({
            submission_id: submissionID,
        });

        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/likeSubmission`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: postBody,
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return await response.json() as LikeSubmissionResponse;
                }

                // If response is not ok, return null to avoid unsafe error return
                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async postCommentOnSubmission(
        post_id: number,
        comment: string
    ): Promise<SubmissionCommentResponse | null>
    {
        const postBody = JSON.stringify({
            post_id,
            comment,
        });

        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/comment`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
            body: postBody,
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return (await response.json()) as SubmissionCommentResponse;
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async likeComment(comment_id: number)
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return {success: false, message: "No token found"};
        }

        const response = await fetch(`${API_ENDPOINT}/comment/like`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: token ? `Bearer ${token}` : "",
            },
            body: JSON.stringify({
                comment_id,
            }),
        }).catch((error) =>
        {
            Logger.error("Error liking comment:", error);

            return false;
        });

        if (typeof response !== "boolean")
        {
            const data = (await response.json()) as DailyHeadlineCommentLikeResponse;

            if (data.status as HTTPStatus !== HTTPStatus.OK)
            {
                Logger.error("Error liking comment:", data.message);

                const {message} = data;

                return {success: false, message};
            }

            return {success: true};
        }

        return {success: false, message: "Unknown error"};
    }

    public async submitDailyHeadlinePhoto(
        headlineID: number,
        photo: string,
        description: string,
        victoryHeadline: string,
    ): Promise<{
        status: number;
        message: string;
    } | null>
    {
        const postBody = JSON.stringify({
            headline_id: headlineID,
            headline_suggestion: victoryHeadline,
            photo_description: description,
            photo_base64: photo,
        });

        const token = localStorage.getItem("access_token");

        const data = await fetch(`${API_ENDPOINT}/submitDailyHeadline`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
            body: postBody,
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return (await response.json()) as { status: number; message: string };
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async getCommentsForSubmission(
        submissionID: number,
        lastCommentID: number,
    )
    {
        const token = localStorage.getItem("access_token");

        const data = await fetch(`${API_ENDPOINT}/getComments?post_id=${submissionID}&last_id=${lastCommentID}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },

        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as SubmissionCommentResponse;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async getPreviousHeadlines(): Promise<{
        status: number;
        message: string;
        data?: PreviousHeadlineSubmission[]
    } | null>
    {
        const data = await fetch(`${API_ENDPOINT}/getPreviousHeadlines`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }).then((resp) =>
        {
            if (!resp.ok)
            {
                throw new Error("Network response was not ok");
            }

            return resp.json() as Promise<{
                status: number;
                message: string;
                data?: PreviousHeadlineSubmission[]
            }>;
        })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        return data;
    }

    public async getProfile(profileID: number)
    {
        const token = localStorage.getItem("access_token");

        const data = await fetch(`${API_ENDPOINT}/profiles?profile_id=${profileID}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as ProfileDataResponse;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async getProfileRealizations(profileID: number, offset = 0)
    {
        const token = localStorage.getItem("access_token");

        const data = await fetch(`${API_ENDPOINT}/profiles/realizations?profile_id=${profileID}&offset=${offset}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
        })
            .then((response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return response.json() as Promise<{
                    status: number;
                    message: string;
                    data: DailyHeadlineSubmission[];
                }>;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async followProfile(profileID: number)
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return {status: 500, message: "No token found"};
        }

        const response = await fetch(`${API_ENDPOINT}/profiles/follow`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
                profile_id: profileID,
            }),
        }).then(async (res) =>
        {
            const data = (await res.json()) as {
                status: number;
                message: string;
                data: {
                    profile_id: number;
                    following_id: number;
                }
            };

            return data;
        })
            .catch((error) =>
            {
                Logger.error("Error following profile:", error);

                return false;
            });

        return response;
    }

    public async uploadAvatar(photo: string): Promise<{
        status: number;
        message: string;
        data?: {
            token: string;
        }
    }| null>
    {
        const postBody = JSON.stringify({
            photo_data: photo,
        });

        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/profiles/uploadProfilePhoto`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: postBody,
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return await response.json() as {
                        status: number;
                        message: string;
                        data?: {
                            token: string;
                        }
                    };
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public saveDetails(displayName: string, bio: string): Promise<{
        status: number;
        message: string;
        data: {
            token: string;
        }
    } | null>
    {
        const postBody = JSON.stringify({
            displayName,
            biography: bio,
        });

        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return Promise.resolve(null);
        }

        return fetch(`${API_ENDPOINT}/profiles/saveDetails`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: postBody,
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return await response.json() as { status: number; message: string, data: { token: string; } };
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });
    }

    public async getRecentProfiles()
    {
        const token = localStorage.getItem("access_token");

        const data = await fetch(`${API_ENDPOINT}/search/recent`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as {
                    status: HTTPStatus;
                    message: string;
                    data?: SearchProfile[]
                };
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async searchRealize(search: string)
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return {status: 500, message: "No token found"};
        }

        const response = await fetch(`${API_ENDPOINT}/search`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
                search,
            }),
        }).then(async (res) =>
        {
            const data = (await res.json()) as {
                status: number;
                message: string;
                data?: SearchProfile[];
            };

            return data;
        })
            .catch((error) =>
            {
                Logger.error("Error searching profile:", error);

                return false;
            });

        return response;
    }

    public async createHeadline(headline: string): Promise<{ status: HTTPStatus; message: string; } | boolean>
    {
        // Remove double asterisks with singles
        const sanatizedHeadline = headline.replace(/\*\*/igu, "*");

        Logger.log(sanatizedHeadline);

        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return {status: 500, message: "No token found"};
        }

        const response = await fetch(`${API_ENDPOINT}/profiles/createHeadline`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
                headline: sanatizedHeadline,
            }),
        }).then(async (res) =>
        {
            const data = (await res.json()) as {
                status: HTTPStatus;
                message: string;
            };

            return data;
        })
            .catch((error) =>
            {
                Logger.error("Error creating headline:", error);

                return false;
            });

        return response;
    }

    public async getHeadlinesForProfile(profileID: number, offset = 0)
    {
        if (profileID === 0)
        {
            return null;
        }

        const token = localStorage.getItem("access_token");

        const data = await fetch(`${API_ENDPOINT}/profiles/headlines?profile_id=${profileID}&offset=${offset}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as ProfileHeadlineResponse;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async getNotifications(offset = 0): Promise<{
        status: HTTPStatus;
        message: string;
        data?: NotificationObject[];
    } | boolean>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return {status: 401, message: "No token found"};
        }

        const response = await fetch(`${API_ENDPOINT}/notifications?offset=${offset}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
            },
        }).then(async (res) =>
        {
            const data = (await res.json()) as {
                status: HTTPStatus;
                message: string;
                data?: NotificationObject[]; // Replace with actual notification type
            };

            return data;
        })
            .catch((error) =>
            {
                Logger.error("Error fetching notifications:", error);

                return false;
            });

        return response;
    }

    public getTotalNotifications(): Promise<{
        status: HTTPStatus;
        message: string;
        data?: number;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return Promise.resolve(null);
        }

        return fetch(`${API_ENDPOINT}/notifications/total`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return await response.json() as { status: HTTPStatus; message: string; data?: number };
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });
    }

    public async markNotificationAsRead(notificationID: number): Promise<{
        status: HTTPStatus;
        message: string;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const response = await fetch(`${API_ENDPOINT}/notifications/mark`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
                notification_id: notificationID,
            }),
        }).then(async (res) =>
        {
            if (res.ok)
            {
                return await res.json() as { status: HTTPStatus; message: string };
            }

            return null;
        })
            .catch((error) =>
            {
                Logger.error("Error marking notification as read:", error);

                return null;
            });

        return response;
    }

    public async markAllNotificationsAsRead(): Promise<{
        status: number;
        message: string;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const response = await fetch(`${API_ENDPOINT}/notifications/mark/all`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        }).then(async (res) =>
        {
            if (res.ok)
            {
                return await res.json() as { status: number; message: string };
            }

            return null;
        })
            .catch((error) =>
            {
                Logger.error("Error marking all notifications as read:", error);

                return null;
            });

        return response;
    }

    public async deleteNotification(notificationID: number): Promise<{
        status: number;
        message: string;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const response = await fetch(`${API_ENDPOINT}/notifications/delete`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
                notification_id: notificationID,
            }),
        }).then(async (res) =>
        {
            if (res.ok)
            {
                return await res.json() as { status: number; message: string };
            }

            return null;
        })
            .catch((error) =>
            {
                Logger.error("Error deleting notification:", error);

                return null;
            });

        return response;
    }

    public async deleteAllNotifications(): Promise<{
        status: number;
        message: string;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const response = await fetch(`${API_ENDPOINT}/notifications/delete/all`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        }).then(async (res) =>
        {
            if (res.ok)
            {
                return await res.json() as { status: number; message: string };
            }

            return null;
        })
            .catch((error) =>
            {
                Logger.error("Error deleting all notifications:", error);

                return null;
            });

        return response;
    }

    public async submitReport(report: ReportedSubmissionDataType, RoomID?: string|null)
    {
        if (!RoomID)
        {
            RoomID = null;
        }

        const postBody = JSON.stringify({
            reportedContent: report.reportedContent,
            roomID: RoomID,
        });

        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/report/submit`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: postBody,
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    const results = await response.json() as { status: number; message: string };

                    Logger.log(results);

                    return response;
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async changeSettings(settings: SettingsStructure)
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        return await fetch(`${API_ENDPOINT}/settings/privacy`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({settings}),
        }).then(async (resp) =>
        {
            return await resp.json() as {
                status: number;
                message: string;
            };
        })
            .catch((error) =>
            {
                Logger.error("Error changing settings:", error);

                return null;
            });
    }

    public async getDailyHeadlineByID(headlineID: number): Promise<{
        status: number;
        message: string;
        data?: DailyHeadline;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/profiles/getHeadlineByID?id=${headlineID}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as { status: number; message: string; data?: DailyHeadline };
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async closeDailyHeadline(id: number): Promise<{
        status: number;
        message: string;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/profiles/closeHeadline`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({headline_id: id}),
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return await response.json() as { status: number; message: string };
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async chooseHeadlineWinner(postID: number, headlineID: number): Promise<{
        status: number;
        message: string;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/profiles/chooseHeadlineWinner`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({post_id: postID, headline_id: headlineID}),
        })
            .then(async (response) =>
            {
                if (response.ok)
                {
                    return await response.json() as { status: number; message: string };
                }

                return null;
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async getPrivacySettings(): Promise<{
        status: number;
        message: string;
        data?: SettingsStructure;
    } | null>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return null;
        }

        const data = await fetch(`${API_ENDPOINT}/settings/privacy`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        })
            .then(async (response) =>
            {
                if (!response.ok)
                {
                    throw new Error("Network response was not ok");
                }

                return await response.json() as { status: number; message: string; data?: SettingsStructure };
            })
            .catch((error) =>
            {
                Logger.error("There was a problem with the fetch operation:", error);

                return null;
            });

        if (!data)
        {
            return null;
        }

        return data;
    }

    public async amFollowingUser(profileID: number): Promise<boolean>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return false;
        }

        const response = await fetch(
            `${API_ENDPOINT}/profiles/amFollowingUser?profileId=${profileID}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
        );

        if (!response.ok)
        {
            Logger.error("Error checking if profile is being followed:", response.statusText);

            return false;
        }

        const data = await response.json() as { status: number; message: string; data?: boolean };

        return data.data ?? false;
    }


    public async profileFollowingMe(profileID: number): Promise<boolean>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.error("No token found in local storage");

            return false;
        }

        const response = await fetch(
            `${API_ENDPOINT}/profiles/profileFollowingMe?profileId=${profileID}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
        );

        if (!response.ok)
        {
            Logger.error("Error checking if profile is being followed:", response.statusText);

            return false;
        }

        const data = await response.json() as { status: number; message: string; data?: boolean };

        return data.data ?? false;
    }
}

export default SocialAPI.getInstance();
